import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../primitives/index.js';
import type { CatalogModel } from '../../lib/api/queries/catalog.js';
import type { EquipmentSearchHit } from '../../lib/api/queries/equipment.js';
import { Icon, machineMotif } from '../icons/index.js';

export interface EquipmentCardProps {
  unit: EquipmentSearchHit;
  /** Resolved from P-Q005; undefined while the catalogue is still loading. */
  model: CatalogModel | undefined;
  /**
   * The catalogue's own name for what this machine IS, resolved from the
   * model's `taxonomy_node_id` against P-Q004. Undefined until the taxonomy
   * lands, and omitted entirely when the node is not among the ones fetched —
   * never guessed from the title, which would be a classification the
   * catalogue did not make.
   */
  category?: string | undefined;
  /**
   * Set only when the search carried a period. The backend answered with
   * `avail.is_free`, so "free on your dates" is a statement about the query
   * that was run — not a reservation, and not a promise.
   */
  matchedPeriod?: boolean;
}

/**
 * One published machine, as the anonymous view (§11.2) permits.
 *
 * The card's job is to make the MACHINE the thing on screen. It cannot do that
 * with a photograph: P-Q011 returns four fields, there is no media on a search
 * hit, and the repository ships no imagery — so a grey image box would be a
 * promise of something that does not exist. What it does instead is give the
 * machine an IDENTITY PLATE: the drawing that stands for its type, the
 * catalogue's name for that type, then the title set as the largest thing on
 * the card, the model beneath it and the build year as a stamp.
 *
 * Every one of those is data the API returned. There is still no price, no
 * location, no distance, no rating and no review here, because the public
 * contract exposes none of them.
 *
 * The whole card is one target. A fact list with a small link at the bottom
 * makes the reader aim at a 20px strip when the entire block means the same
 * thing; the link is stretched over the card instead, so the title stays the
 * accessible name and the pointer gets the full area.
 */
/**
 * A catalogue code as a phrase a person reads.
 *
 * `operator_required` becomes "Operator required", never "Operator Required":
 * title case on a machine attribute reads as a proper noun and these are not
 * proper nouns. Done here rather than with `text-transform: capitalize`, which
 * capitalises every word and cannot know which one is first.
 */
function sentenceCase(code: string): string {
  const words = code.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function EquipmentCard({
  unit,
  model,
  category,
  matchedPeriod,
}: EquipmentCardProps): ReactElement {
  const modelLine =
    model === undefined
      ? null
      : [model.manufacturer, model.model_name, model.model_variant].filter(Boolean).join(' ');

  return (
    <li className="eq-card liftable">
      {/*
        The plate where a photograph would go is a DRAWING, not a picture: the
        icon that stands for this machine's type, on the sunken ground. It
        carries no information the words beside it do not, so it is hidden from
        assistive technology.
      */}
      <span className="eq-card__plate" aria-hidden="true">
        <span className="eq-card__glyph">
          <Icon name={machineMotif(unit.display_title)} size="lg" />
        </span>
      </span>

      <div className="eq-card__body">
        {category === undefined ? null : <p className="eq-card__type">{category}</p>}

        {/* h2: the page's h1 is its title and a card sits beneath it. The
            visual size comes from the class, not from the level. */}
        <h2 className="eq-card__title">
          <Link to={`/equipment/${unit.id}`} className="eq-card__link">
            {unit.display_title}
          </Link>
        </h2>

        {modelLine === null ? null : <p className="eq-card__model">{modelLine}</p>}

        <dl className="eq-card__facts">
          {unit.year_of_manufacture === null ? null : (
            <div className="eq-card__fact">
              <dt>Year</dt>
              <dd className="numeric">{unit.year_of_manufacture}</dd>
            </div>
          )}
          {model === undefined || model.operator_requirement === null ? null : (
            <div className="eq-card__fact">
              <dt>Operator</dt>
              <dd>{sentenceCase(model.operator_requirement)}</dd>
            </div>
          )}
        </dl>

        <div className="eq-card__foot">
          {matchedPeriod === true ? (
            <p className="eq-card__signal">
              <Badge tone="success">Free on your dates</Badge>
              <span className="eq-card__signal-note">
                Informational only — dates are held when a booking is made.
              </span>
            </p>
          ) : null}
          <p className="eq-card__cue" aria-hidden="true">
            View machine <Icon name="arrow" size="sm" />
          </p>
        </div>
      </div>
    </li>
  );
}
